import React, { useCallback, useEffect, useState } from 'react';
import { Check, X, Crop, Palette, Pencil, FolderPlus, Download, Trash2, Star, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { formatApiError } from '../lib/apiError';
import PhotoEditModal from './PhotoEditModal';
import { PhotoBackgroundModal, PhotoDetailsModal, AlbumAssignModal } from './PhotoOpsModals';

// Shared admin "gallery operations" for a photo: approve/reject, crop/rotate/resize (display-only or
// saved to gallery), background & transparency, edit details, album assignment, download, delete.
// Returns menu items for PhotoActionsMenu plus the modals to render. `onChanged(updatedPhotoOrNull)`
// fires after any successful change so the caller can refresh.
export default function usePhotoOps(onChanged) {
  const auth = useAuth();
  const [modal, setModal] = useState(null); // {kind, photo} | {kind:'album', ids}
  const [albums, setAlbums] = useState([]);
  const close = useCallback(() => setModal(null), []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { auth.adminPhotoAlbums().then(setAlbums).catch(() => {}); }, []);
  const refreshAlbums = () => auth.adminPhotoAlbums().then(setAlbums).catch(() => {});

  const moderate = async (photo, action) => {
    let note = '';
    if (action === 'reject') { note = window.prompt('Optional reason (shown to the member):') || ''; }
    try { await auth.moderatePhoto(photo.id, action, note); toast.success(action === 'approve' ? 'Photo approved.' : 'Photo rejected.'); onChanged({ ...photo, status: action === 'approve' ? 'approved' : 'rejected' }); }
    catch { toast.error(`Could not ${action} the photo.`); }
  };
  const patch = async (photo, body, msg) => {
    try { const r = await auth.adminUpdatePhoto(photo.id, body); toast.success(msg); onChanged(r); return r; }
    catch (e) { toast.error(formatApiError(e, 'Could not save.')); throw e; }
  };
  const download = (photo) => { const a = document.createElement('a'); a.href = auth.mediaUrl(photo.url); a.download = `${photo.title || photo.id}.jpg`; a.target = '_blank'; a.rel = 'noopener'; document.body.appendChild(a); a.click(); a.remove(); };
  const del = async (photo) => {
    if (!window.confirm(`Delete "${photo.title || 'this photo'}" permanently? This cannot be undone.`)) return;
    try { await auth.adminDeletePhoto(photo.id); toast.success('Photo deleted.'); onChanged(null, photo.id); }
    catch { toast.error('Could not delete the photo.'); }
  };

  const aiDescribe = async (photo) => {
    const overwrite = (photo.title || photo.description) ? window.confirm('This photo already has a title/description. OK = replace them with the suggested text, Cancel = only fill empty fields.') : false;
    const t = toast.loading('Describing this photo…');
    try { const r = await auth.adminAiDescribePhoto(photo.id, overwrite); toast.success(r.changed.length ? `Updated ${r.changed.join(', ')}.` : 'Nothing new to add.', { id: t }); onChanged(r.photo); }
    catch (e) { toast.error(formatApiError(e, 'Image description failed.'), { id: t }); }
  };

  const actionsFor = (photo, { extra = [], moderation = true, allowDelete = true } = {}) => [
    moderation && photo.status !== 'approved' ? { label: 'Approve', icon: Check, onClick: () => moderate(photo, 'approve'), testId: `op-approve-${photo.id}` } : null,
    moderation && photo.status !== 'rejected' ? { label: 'Reject', icon: X, onClick: () => moderate(photo, 'reject'), testId: `op-reject-${photo.id}` } : null,
    { label: 'Edit image (crop / rotate / flip / adjust)', icon: Crop, onClick: () => setModal({ kind: 'edit', photo }), testId: `op-edit-image-${photo.id}` },
    { label: 'Background & transparency', icon: Palette, onClick: () => setModal({ kind: 'bg', photo }), testId: `op-bg-${photo.id}` },
    { label: 'Edit details', icon: Pencil, onClick: () => setModal({ kind: 'details', photo }), testId: `op-details-${photo.id}` },
    { label: 'Description & tags', icon: Sparkles, onClick: () => aiDescribe(photo), testId: `op-ai-${photo.id}` },
    { label: 'Albums…', icon: FolderPlus, onClick: () => setModal({ kind: 'album', ids: [photo.id], current: photo.albums || (photo.album ? [photo.album] : []) }), testId: `op-album-${photo.id}` },
    ...((photo.albums && photo.albums.length ? photo.albums : (photo.album ? [photo.album] : [])).slice(0, 4).map((al) => ({ label: `Set as cover of “${al}”`, icon: Star, onClick: () => auth.adminSetAlbumMeta(al, { cover_photo_id: photo.id }).then(() => toast.success('Album cover updated.')).catch((e) => toast.error(formatApiError(e, 'Could not set the cover.'))), testId: `op-cover-album-${photo.id}-${al}` }))),
    ...extra,
    { label: 'Download', icon: Download, onClick: () => download(photo), testId: `op-download-${photo.id}` },
    allowDelete ? { label: 'Delete', icon: Trash2, danger: true, onClick: () => del(photo), testId: `op-delete-${photo.id}` } : null,
  ];

  const openAlbumFor = (ids, current = []) => setModal({ kind: 'album', ids, current });

  const assignAlbum = async ({ add, remove }) => {
    try {
      for (const name of add) await auth.adminAlbumAddPhotos(name, modal.ids);
      for (const name of remove) await auth.adminAlbumRemovePhotos(name, modal.ids);
      const n = modal.ids.length;
      toast.success([add.length ? `Added ${n} photo${n !== 1 ? 's' : ''} to ${add.map((a) => `“${a}”`).join(', ')}` : '', remove.length ? `removed from ${remove.map((a) => `“${a}”`).join(', ')}` : ''].filter(Boolean).join('; ') + '.');
      close(); refreshAlbums(); onChanged();
    } catch (e) { toast.error(formatApiError(e, 'Could not update albums.')); }
  };

  const modals = modal ? (
    <>
      {modal.kind === 'edit' && (
        <PhotoEditModal src={auth.mediaUrl(modal.photo.url)} allowDisplayOnly title="Edit image" initialView={(modal.photo.display || {}).view}
          onCancel={close}
          onConfirm={async (dataUrl) => { try { const r = await auth.adminReplacePhotoImage(modal.photo.id, dataUrl); toast.success('Edited image saved to the gallery.'); close(); onChanged(r); } catch { toast.error('Could not save the edited image.'); } }}
          onConfirmView={async (view) => { await patch(modal.photo, { display: { ...(modal.photo.display || {}), view } }, 'Display settings saved (original file kept).'); close(); }} />
      )}
      {modal.kind === 'bg' && <PhotoBackgroundModal photo={modal.photo} src={auth.mediaUrl(modal.photo.url)} onClose={close} onSave={async (display) => { await patch(modal.photo, { display }, 'Background & transparency saved.'); close(); }} />}
      {modal.kind === 'details' && <PhotoDetailsModal photo={modal.photo} albums={albums} onClose={close} onSave={async (body) => { await patch(modal.photo, body, 'Photo details saved.'); close(); refreshAlbums(); }} />}
      {modal.kind === 'album' && <AlbumAssignModal count={modal.ids.length} albums={albums} current={modal.current || []} onClose={close} onSave={assignAlbum} />}
    </>
  ) : null;

  return { actionsFor, modals, openAlbumFor, moderate, download, del, aiDescribe, albums };
}
